import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresAdminPassword, setRequiresAdminPassword] = useState(false);
  const navigate = useNavigate();

  // Check for existing session on load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await api.get('/api/auth/session', {
          headers: {
            'x-admin-password': localStorage.getItem('adminPassword') || ''
          }
        });

        const data = response.data;

        if (data.requiresAdminPassword) {
          setRequiresAdminPassword(true);
          setIsLoading(false);
          return;
        }

        setUser(data.user || data); // Handle both response formats
        // Store admin password if this was an admin login
        if (data.isAdmin && (data.user?.email === 'aminboulabaim@gmail.com' || data.email === 'aminboulabaim@gmail.com')) {
          const password = response.headers['x-admin-password'];
          if (password) {
            localStorage.setItem('adminPassword', password);
          }
        }
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Session check failed:', error.response?.data || error.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email, password, adminPassword) => {
    try {
      const headers = {};

      // If this is an admin login with password
      if (adminPassword) {
        headers['x-admin-password'] = adminPassword;
      }

      const response = await api.post('/api/auth/login',
        { email, password },
        { headers }
      );

      const userData = response.data.user || response.data;
      setUser(userData);

      if (adminPassword && userData.isAdmin && (userData.email === 'aminboulabaim@gmail.com' || userData.user?.email === 'aminboulabaim@gmail.com')) {
        localStorage.setItem('adminPassword', adminPassword);
      }

      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error.response?.data || error.message);
    } finally {
      localStorage.removeItem('adminPassword');
      setUser(null);
      navigate('/login');
    }
  };

  // The backend fully manages the session bypassing logic, so no client-side hardcoding needed
  const value = {
    user,
    isLoading,
    requiresAdminPassword,
    login,
    logout,
    isAuthenticated: !!user
  };

  return (
    <UserContext.Provider value={value}>
      {!isLoading && children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};